import React from "react"
import { twMerge } from "tailwind-merge"

import AnimatedSection from "../../ProProfileShared/AnimatedSection"
import { getReviewDisplayData, renderStarRating } from "../utils/reviewUtils"
import { applyButtonTheme, getButtonClasses } from "../utils/themeUtils"
import { DEFAULT_HIGHLIGHT_CLASSES } from "../WebsiteBuilderV2Page"

import HeaderSectionDesktop from "./HeaderSectionDesktop"

// Helper function to determine text color based on background
const getContrastingTextColor = (layout, hasBackgroundImage, theme, isInWhiteContainer = false) => {
  // If content is in a white container (like scattered_images with multiple images), use dark text
  if (isInWhiteContainer) {
    return theme?.colors?.textDark || "#000000"
  }
  // If there's a background image or it's a full image layout, use light text
  if (hasBackgroundImage || layout === "full_image" || layout.includes("image")) {
    return theme?.colors?.textLight || "#FFFFFF"
  }
  // Otherwise use dark text for light backgrounds
  return theme?.colors?.textDark || "#55433F"
}

const SubHeroSectionDesktop = ({ section, practice, theme, highlightedField }) => {
  const isHighlighted = highlightedField === `section-${section.id}`
  const settings = section.settings || {}
  const layout = settings.layout || "full_image"
  const overlayOpacity = settings.overlayOpacity || 30
  const showCTA = settings.showCTA !== undefined ? settings.showCTA : true

  const ctaText = settings.ctaText || "Learn More"
  const ctaAction = settings.ctaAction || "page"
  const ctaTarget = settings.ctaTarget || ""

  // Get review data from practice
  const reviewData = getReviewDisplayData(practice, settings.showReviews)
  const showReviews = settings.showReviews !== undefined ? settings.showReviews : reviewData.showReviews

  const handleCTAClick = () => {
    const scrollToLayout = (layoutId) => {
      if (!layoutId) return
      const targetElement = document.querySelector(`[id^=section-${layoutId}-]`)
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: "smooth" })
      }
    }

    switch (ctaAction) {
      case "page": {
        if (ctaTarget) {
          const { origin, pathname } = window.location
          const parts = pathname.split("/").filter(Boolean)
          if (parts.length >= 2 && parts[0] !== "portal") {
            const targetUrl = `${origin}/${parts[0]}/${ctaTarget}`
            window.location.href = targetUrl
          } else {
            window.location.href = `/${ctaTarget}`
          }
        }
        break
      }
      case "service":
        if (ctaTarget) {
          window.open(`/services/${ctaTarget}`, "_blank")
        } else {
          window.open("/services", "_blank")
        }
        break
      case "booking":
        window.open("/services", "_blank")
        break
      case "contact":
        scrollToLayout("contact")
        break
      case "scroll":
        window.scrollBy({ top: window.innerHeight, behavior: "smooth" })
        break
      case "external":
        if (ctaTarget) {
          const url = ctaTarget.startsWith("http") ? ctaTarget : `https://${ctaTarget}`
          window.open(url, "_blank")
        }
        break
      default:
        break
    }
  }

  const renderContent = (hasBackgroundImage = false, customLayout = null, isInWhiteContainer = false) => {
    const currentLayout = customLayout || layout
    const textColor = getContrastingTextColor(currentLayout, hasBackgroundImage, theme, isInWhiteContainer)

    return (
      <div className="flex flex-col">
        <div className="flex flex-col gap-3">
          {section.headline && (
            <h1
              className="w-full"
              style={{
                fontSize: settings.headlineSize || "56px",
                fontFamily: theme?.fonts?.heading || "Playfair Display, serif",
                fontWeight: theme?.fonts?.headingWeight || "700",
                color: settings.headlineColor || textColor,
                lineHeight: "1.1",
                textAlign: settings.headingAlign || settings.textAlign || "left"
              }}>
              {section.headline}
            </h1>
          )}
          {section.subheadline && (
            <p
              className="w-full"
              style={{
                fontSize: settings.subheadlineSize || "20px",
                fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                fontWeight: theme?.fonts?.paragraphRegularWeight || "400",
                color: settings.subheadlineColor || textColor,
                textAlign: settings.subheadingAlign || settings.textAlign || "left"
              }}>
              {section.subheadline}
            </p>
          )}
        </div>

        <div
          className="flex flex-col gap-4"
          style={{
            alignItems:
              settings.ctaAlign === "center" ? "center" : settings.ctaAlign === "right" ? "flex-end" : "flex-start",
            marginTop: settings.ctaTopMargin ? `${settings.ctaTopMargin}px` : "48px"
          }}>
          {showCTA && (
            <button
              type="button"
              onClick={handleCTAClick}
              className={twMerge(
                "inline-flex items-center justify-center px-10 py-3 text-[16px] font-normal leading-[24px] text-white transition-all hover:shadow-lg",
                getButtonClasses(theme?.buttonStyle)
              )}
              style={applyButtonTheme(
                {
                  backgroundColor: theme?.colors?.primary || "#BD654F",
                  fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif"
                },
                theme
              )}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = theme?.colors?.primaryDark || "#A55A45"
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = theme?.colors?.primary || "#BD654F"
              }}>
              {ctaText}
            </button>
          )}

          {showReviews && (
            <div className="flex items-center">
              <div className="mr-2 flex h-[20px] items-start">
                {renderStarRating(reviewData.averageRating, "h-[20px] w-[20px]")}
              </div>
              <span
                className="text-[16px] font-semibold leading-[24px] underline"
                style={{ color: settings.reviewTextColor || settings.subheadlineColor || textColor }}>
                {reviewData.reviewCount} verified review{reviewData.reviewCount !== 1 ? "s" : ""}
              </span>
            </div>
          )}
        </div>
      </div>
    )
  }

  switch (layout) {
    case "image_left":
      return (
        <section
          className={twMerge(
            "relative flex items-center justify-start",
            isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
          )}
          style={{
            backgroundColor: theme?.colors?.bgLight || "#FFFFFF"
          }}>
          <AnimatedSection className="mx-auto w-full px-[140px] py-[4.5rem]">
            <div className="flex h-[600px] items-center">
              {section.imageUrl || settings.images?.[0]?.url ? (
                <div
                  className="h-[600px] w-1/2 bg-cover bg-center bg-no-repeat"
                  style={{ backgroundImage: `url('${section.imageUrl || settings.images?.[0]?.url}')` }}
                />
              ) : (
                <div className="flex h-[600px] w-1/2 items-center justify-center bg-gray-200">
                  <span className="text-gray-500">No image selected</span>
                </div>
              )}
              <div className="flex w-1/2 flex-col items-start justify-center gap-[48px] px-[52px]">
                {renderContent(false)}
              </div>
            </div>
          </AnimatedSection>
        </section>
      )

    case "image_right":
      return (
        <section
          className={twMerge(
            "relative flex items-center justify-start",
            isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
          )}
          style={{
            backgroundColor: theme?.colors?.bgLight || "#FFFFFF"
          }}>
          <AnimatedSection className="mx-auto w-full px-[140px] py-[4.5rem]">
            <div className="flex h-[600px] items-center">
              <div className="flex w-1/2 flex-col items-start justify-center gap-[48px] px-[52px]">
                {renderContent(false)}
              </div>
              {section.imageUrl || settings.images?.[0]?.url ? (
                <div
                  className="h-[600px] w-1/2 bg-cover bg-center bg-no-repeat"
                  style={{ backgroundImage: `url('${section.imageUrl || settings.images?.[0]?.url}')` }}
                />
              ) : (
                <div className="flex h-[600px] w-1/2 items-center justify-center bg-gray-200">
                  <span className="text-gray-500">No image selected</span>
                </div>
              )}
            </div>
          </AnimatedSection>
        </section>
      )

    case "half_image_left": {
      return (
        <section
          className={twMerge("relative flex h-[728px] items-center", isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : "")}
          style={{
            backgroundColor: theme?.colors?.bgLight || "#FFFFFF"
          }}>
          <AnimatedSection className="mx-auto w-full py-[4.5rem]">
            <div className="flex h-[728px] items-center">
              {/* Image on the left - half width */}
              {section.imageUrl || settings.images?.[0]?.url ? (
                <div
                  className="h-[728px] w-1/2 bg-cover bg-center bg-no-repeat"
                  style={{ backgroundImage: `url('${section.imageUrl || settings.images?.[0]?.url}')` }}
                />
              ) : (
                <div className="flex h-[728px] w-1/2 items-center justify-center bg-gray-200">
                  <span className="text-gray-500">No image selected</span>
                </div>
              )}
              {/* Content on the right - half width with white background */}
              <div className="flex h-[728px] w-1/2 shrink-0 flex-col items-start justify-center gap-[48px] bg-white px-[80px] py-[137px]">
                {renderContent(false, null, true)}
              </div>
            </div>
          </AnimatedSection>
        </section>
      )
    }

    case "half_image_right": {
      return (
        <section
          className={twMerge("relative flex h-[728px] items-center", isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : "")}
          style={{
            backgroundColor: theme?.colors?.bgLight || "#FFFFFF"
          }}>
          <AnimatedSection className="mx-auto w-full py-[4.5rem]">
            <div className="flex h-[728px] items-center">
              {/* Content on the left - half width with white background */}
              <div className="flex h-[728px] w-1/2 shrink-0 flex-col items-start justify-center gap-[48px] bg-white px-[80px] py-[137px]">
                {renderContent(false, null, true)}
              </div>
              {/* Image on the right - half width */}
              {section.imageUrl || settings.images?.[0]?.url ? (
                <div
                  className="h-[728px] w-1/2 shrink-0 bg-cover bg-center bg-no-repeat"
                  style={{ backgroundImage: `url('${section.imageUrl || settings.images?.[0]?.url}')` }}
                />
              ) : (
                <div className="flex h-[728px] w-1/2 shrink-0 items-center justify-center bg-gray-200">
                  <span className="text-gray-500">No image selected</span>
                </div>
              )}
            </div>
          </AnimatedSection>
        </section>
      )
    }

    case "scattered_images": {
      const images = section.settings?.images || []
      const imageCount = images.length

      // Case 1: Single image - use full image layout
      if (imageCount === 1) {
        return (
          <section
            className={twMerge(
              "relative flex min-h-[504px] items-center justify-center",
              isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
            )}
            style={{
              backgroundColor: theme?.colors?.bgLight || "#FFFFFF"
            }}>
            {images[0]?.url && (
              <div
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: `url(${images[0].url})` }}
              />
            )}
            <div className="absolute inset-0" style={{ backgroundColor: `rgba(0, 0, 0, ${overlayOpacity / 100})` }} />
            <AnimatedSection className="relative z-10 mx-auto max-w-4xl px-4">
              {renderContent(true, "full_image")}
            </AnimatedSection>
          </section>
        )
      }

      // Case 2: Two images - overlapped layout with centered content (based on Figma)
      if (imageCount === 2) {
        return (
          <section
            className={twMerge(
              "relative flex min-h-[480px] items-center justify-center py-[4.5rem]",
              isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
            )}
            style={{
              backgroundColor: theme?.colors?.bgLight || "#FFFFFF"
            }}>
            <div className="relative mx-auto w-full max-w-[1200px] px-0">
              {/* Desktop: Overlapped layout */}
              <div>
                {/* First image - left side, more centered */}
                <div
                  className="absolute h-[300px] w-[280px] rounded-lg bg-cover bg-center"
                  style={{
                    left: "80px",
                    top: "50px",
                    backgroundImage: `url(${images[0]?.url})`
                  }}
                />
                {/* Second image - right side, vertically centered with content */}
                <div
                  className="absolute right-[60px] top-1/2 h-[280px] w-[320px] -translate-y-1/2 rounded-lg bg-cover bg-center"
                  style={{
                    backgroundImage: `url(${images[1]?.url})`
                  }}
                />
              </div>

              {/* Content container */}
              <div
                className="relative left-1/2 top-1/2 z-10 w-full -translate-x-1/2 -translate-y-1/2 bg-white px-12 py-10 text-center"
                style={{
                  boxShadow: "0 4px 20px rgba(0,0,0,0.1)"
                }}>
                <AnimatedSection>{renderContent(false, null, true)}</AnimatedSection>
              </div>
            </div>
          </section>
        )
      }

      // Case 3: Three+ images - Figma scattered layout
      return (
        <section
          className={twMerge(
            "relative flex min-h-[600px] items-center justify-center py-[4.5rem]",
            isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
          )}
          style={{
            backgroundColor: theme?.colors?.bgLight || "#FFFFFF"
          }}>
          <div className="relative mx-auto h-[600px] w-full max-w-[1440px] px-0">
            {/* Desktop: Figma scattered layout - centered content with 3 scattered images */}

            {/* Centered content - matches Figma exactly */}
            <div
              className="absolute left-1/2 top-1/2 z-20 w-[517px] -translate-x-1/2 -translate-y-1/2 rounded-lg bg-white px-8 py-12 text-center"
              style={{
                boxShadow: "0 8px 32px rgba(0,0,0,0.15)"
              }}>
              <AnimatedSection>{renderContent(false, null, true)}</AnimatedSection>
            </div>

            {/* First image - top left (matches Figma positioning) */}
            {images.length > 0 && (
              <div
                className="absolute h-[216px] w-[279px] rounded-lg bg-cover bg-center shadow-lg"
                style={{
                  left: "136px",
                  top: "63px",
                  backgroundImage: `url(${images[0]?.url})`
                }}
              />
            )}

            {/* Second image - top right (matches Figma positioning) */}
            {images.length > 1 && (
              <div
                className="absolute h-[326px] w-[422px] rounded-lg bg-cover bg-center shadow-lg"
                style={{
                  left: "1041px",
                  top: "75px",
                  backgroundImage: `url(${images[1]?.url})`
                }}
              />
            )}

            {/* Third image - bottom left with rotation (matches Figma exactly) */}
            {images.length > 2 && (
              <div
                className="absolute h-[384px] w-[496px] rounded-lg bg-cover bg-center shadow-lg"
                style={{
                  left: "-197px",
                  top: "206px",
                  transform: "rotate(180deg) scaleY(-1)",
                  backgroundImage: `url(${images[2]?.url})`
                }}
              />
            )}
          </div>
        </section>
      )
    }

    case "overlap_double_image": {
      const images = section.settings?.images || []
      // Two images overlapped with centered content
      if (images.length >= 2) {
        return (
          <section
            className={twMerge(
              "relative flex min-h-[480px] items-center justify-center py-[4.5rem]",
              isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
            )}
            style={{ backgroundColor: theme?.colors?.bgLight || "#FFFFFF" }}>
            <div className="relative mx-auto w-full max-w-[1200px] px-0">
              {/* Desktop: Figma-inspired layout - larger images with centered content */}
              <div className="relative flex h-full items-center justify-center">
                {/* Left image - larger size to match Figma */}
                <div
                  className="absolute h-[385px] w-[385px] rounded-lg bg-cover bg-center shadow-lg"
                  style={{
                    left: "0px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    backgroundImage: `url(${images[0]?.url})`,
                    zIndex: 1
                  }}
                />

                {/* Center content card - wider to match Figma */}
                <div
                  className="relative z-20 w-[626px] bg-white p-14 text-center shadow-lg"
                  style={{
                    boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
                    minHeight: "376px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}>
                  <AnimatedSection>{renderContent(false, null, true)}</AnimatedSection>
                </div>

                {/* Right image - larger size with rotation/flip like Figma */}
                <div
                  className="absolute h-[385px] w-[385px] rounded-lg bg-cover bg-center shadow-lg"
                  style={{
                    right: "0px",
                    top: "50%",
                    transform: "translateY(-50%) rotate(180deg) scaleY(-1)",
                    backgroundImage: `url(${images[1]?.url})`,
                    zIndex: 1
                  }}
                />
              </div>
            </div>
          </section>
        )
      }
      // One image: fall back to full image style with overlay
      if (images.length === 1) {
        return (
          <section
            className={twMerge(
              "relative flex min-h-[504px] items-center justify-center",
              isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
            )}
            style={{ backgroundColor: theme?.colors?.primary || "#3B82F6" }}>
            {images[0]?.url && (
              <div
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: `url(${images[0].url})` }}
              />
            )}
            <div className="absolute inset-0" style={{ backgroundColor: `rgba(0, 0, 0, ${overlayOpacity / 100})` }} />
            <AnimatedSection className="relative z-10 mx-auto max-w-4xl px-4">
              {renderContent(true, "full_image")}
            </AnimatedSection>
          </section>
        )
      }
      // No images: content only
      return (
        <section
          className={twMerge(
            "relative flex min-h-[413px] items-center justify-center py-[4.5rem]",
            isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
          )}
          style={{ backgroundColor: theme?.colors?.bgLight || "#FFFFFF" }}>
          <AnimatedSection className="mx-auto max-w-4xl px-4">{renderContent(false, null, true)}</AnimatedSection>
        </section>
      )
    }

    case "contained_image":
      return (
        <section
          className={twMerge(
            "relative flex min-h-[504px] items-center justify-center p-8",
            isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
          )}
          style={{ backgroundColor: theme?.colors?.bgLight || "#FFFFFF" }}>
          {/* Image container with rounded corners */}
          <div className="absolute inset-8 overflow-hidden rounded-lg">
            {(section.imageUrl || settings.images?.[0]?.url) && (
              <div
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: `url(${section.imageUrl || settings.images?.[0]?.url})` }}
              />
            )}
            <div className="absolute inset-0" style={{ backgroundColor: `rgba(0, 0, 0, ${overlayOpacity / 100})` }} />
          </div>

          {/* Content overlaid on the image */}
          <AnimatedSection className="relative z-10 mx-auto max-w-4xl px-4">
            {renderContent(true, "contained_image")}
          </AnimatedSection>
        </section>
      )

    case "full_image":
      return (
        <section
          className={twMerge(
            "relative flex min-h-[504px] items-center justify-center",
            isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
          )}
          style={{ backgroundColor: theme?.colors?.primary || "#3B82F6" }}>
          {(section.imageUrl || settings.images?.[0]?.url) && (
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${section.imageUrl || settings.images?.[0]?.url})` }}
            />
          )}
          <div className="absolute inset-0" style={{ backgroundColor: `rgba(0, 0, 0, ${overlayOpacity / 100})` }} />

          {/* Header superpuesto para full_image */}
          <div className="absolute inset-x-0 top-0 z-20">
            <HeaderSectionDesktop
              section={{ id: "subhero-header", settings: { showPromoBanner: false } }}
              practice={practice}
              theme={theme}
              highlightedField={null}
              transparent={true}
            />
          </div>

          <AnimatedSection className="relative z-10 mx-auto max-w-4xl px-4">
            {renderContent(true, "full_image")}
          </AnimatedSection>
        </section>
      )
  }
}

export default SubHeroSectionDesktop
